import React, { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import Modal from 'components/Modal';
import Button from 'components/Button';
import TripActions from 'store/ducks/trip';
import { actions as toastrActions } from 'react-redux-toastr';
import parser from 'fast-xml-parser';
import he from 'he';
import { Container, WrapperButtons, FileInput, FileList, FileItem } from './styles';

function ModalImportarMultiplosXml({ open, onClose, onSuccess }) {
  const dispatch = useDispatch();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ processed: 0, total: 0, errors: [] });
  const fileInputRef = useRef(null);

  const addXmltoJson = (Xmlcte) => {
    const options = {
      attributeNamePrefix: '@_',
      attrNodeName: 'attr',
      textNodeName: '#text',
      ignoreAttributes: true,
      ignoreNameSpace: false,
      allowBooleanAttributes: false,
      parseNodeValue: true,
      parseAttributeValue: false,
      trimValues: true,
      cdataTagName: '__cdata',
      cdataPositionChar: '\\c',
      parseTrueNumberOnly: false,
      numParseOptions: {
        hex: true,
        leadingZeros: true,
      },
      arrayMode: false,
      attrValueProcessor: (val, attrName) =>
        he.decode(val, { isAttributeValue: true }),
      tagValueProcessor: (val, tagName) => he.decode(val),
      stopNodes: ['parse-me-as-string'],
      alwaysCreateTextNode: false,
    };

    if (parser.validate(Xmlcte) === true) {
      let jsonObj = parser.parse(Xmlcte, options);
    }

    const tObj = parser.getTraversalObj(Xmlcte, options);
    return parser.convertToJson(tObj, options);
  };

  const processXmlToViagem = (binaryStr) => {
    const jsonCte = addXmltoJson(binaryStr);
    
    if (!jsonCte) {
      throw new Error('XML inválido');
    }

    const { NFe } = jsonCte?.NFe ? jsonCte : jsonCte.nfeProc;

    if (binaryStr.indexOf('Id=') < 0 || !NFe) {
      throw new Error('Arquivo XML inválido ou não é uma NFe');
    }

    const defaultCpf = '0'.repeat(11);
    const defaultCnpj = '0'.repeat(14);

    // Extrair chave da NFe
    let chaveNfe = '';
    try {
      // Método 1: Regex para encontrar Id="NFe" seguido de 44 dígitos
      const idMatch = binaryStr.match(/Id=["']NFe(\d{44})["']/);
      if (idMatch && idMatch[1] && idMatch[1].length === 44) {
        chaveNfe = idMatch[1];
        console.log('✅ Chave NFe extraída via regex:', chaveNfe);
      } else {
        // Método 2: Tentar extrair do JSON parseado
        if (NFe && NFe.infNFe) {
          // Pode estar em diferentes formatos dependendo do parser
          const idValue = NFe.infNFe['@_Id'] || NFe.infNFe.Id || (NFe.infNFe.attr && NFe.infNFe.attr.Id);
          if (idValue) {
            if (typeof idValue === 'string' && idValue.startsWith('NFe')) {
              chaveNfe = idValue.replace('NFe', '').substring(0, 44);
              console.log('✅ Chave NFe extraída do JSON:', chaveNfe);
            }
          }
        }
        
        // Método 3: Extração manual do XML
        if (!chaveNfe || chaveNfe.length !== 44) {
          const patterns = [
            /Id=["']NFe(\d{44})["']/,
            /Id=["']NFe(\d{44})/,
            /Id=NFe(\d{44})/,
          ];
          
          for (const pattern of patterns) {
            const match = binaryStr.match(pattern);
            if (match && match[1] && match[1].length === 44) {
              chaveNfe = match[1];
              console.log('✅ Chave NFe extraída via padrão alternativo:', chaveNfe);
              break;
            }
          }
        }
        
        // Método 4: Busca manual por índice
        if (!chaveNfe || chaveNfe.length !== 44) {
          const idIndex = binaryStr.indexOf('Id="NFe');
          if (idIndex >= 0) {
            const startIndex = idIndex + 7; // Pula 'Id="NFe'
            const extracted = binaryStr.substr(startIndex, 44);
            if (extracted && extracted.length === 44 && /^\d{44}$/.test(extracted)) {
              chaveNfe = extracted;
              console.log('✅ Chave NFe extraída via índice:', chaveNfe);
            }
          }
        }
      }
      
      // Validar se a chave tem 44 dígitos
      if (chaveNfe && chaveNfe.length !== 44) {
        console.warn('⚠️ Chave NFe extraída não tem 44 dígitos:', chaveNfe.length, chaveNfe);
        chaveNfe = ''; // Limpar se inválida
      }
    } catch (chaveError) {
      console.error('❌ Erro ao extrair chave NFe:', chaveError);
    }

    // Usar chave para referência (fallback se não conseguir extrair)
    const chave = chaveNfe || (binaryStr.indexOf('Id=') >= 0 ? binaryStr.substr(binaryStr.indexOf('Id=') + 7, 44) : '');
    
    // Log final para debug
    if (chaveNfe) {
      console.log('📋 Chave NFe final que será salva:', chaveNfe);
    } else {
      console.warn('⚠️ Chave NFe não foi extraída. Tentando fallback...');
    }

    // Criar invoice a partir do XML
    const invoice = {
      cnpj: NFe.infNFe.dest.CPF
        ? (defaultCpf + NFe.infNFe.dest.CPF).slice(-11).replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
        : (defaultCnpj + NFe.infNFe.dest.CNPJ).slice(-14).replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5'),
      barcode: chave,
      nfeValue: NFe.infNFe.pag?.detPag?.vPag || NFe.infNFe.total?.ICMSTot?.vNF || 0,
      volume: NFe.infNFe.transp?.vol?.qVol || 0,
      weight: NFe.infNFe.transp?.vol?.pesoB || 0,
      payment_responser: NFe.infNFe.transp?.modFrete === 0 ? '1' : '2',
      data: {
        city: NFe.infNFe.dest?.enderDest?.xMun?.trim() || '',
        complement: NFe.infNFe.dest?.enderDest?.xCpl?.trim() || '',
        country: 'Brasil',
        district: NFe.infNFe.dest?.enderDest?.xBairro?.trim() || '',
        name: NFe.infNFe.dest?.xNome?.trim() || '',
        number: NFe.infNFe.dest?.enderDest?.nro?.toString() || 'SN',
        state: NFe.infNFe.dest?.enderDest?.UF?.trim() || '',
        street: NFe.infNFe.dest?.enderDest?.xLgr?.trim() || '',
        zipcode: NFe.infNFe.dest?.enderDest?.CEP?.toString() || '',
      },
    };

    if (NFe.infNFe.dest?.CPF) {
      invoice.receiver = {
        name: NFe.infNFe.dest.xNome,
        city: NFe.infNFe.dest.enderDest.xMun,
        complement: NFe.infNFe.dest.enderDest.xCpl || '',
        neighborhood: NFe.infNFe.dest.enderDest.xBairro,
        number: NFe.infNFe.dest.enderDest.nro || 'SN',
        state: NFe.infNFe.dest.enderDest.UF,
        street: NFe.infNFe.dest.enderDest.xLgr,
        zipcode: NFe.infNFe.dest.enderDest.CEP,
        phone: NFe.infNFe.dest.enderDest.fone?.toString() || '',
        email: NFe.infNFe.dest.email || '',
      };
    }

    // Criar estrutura da viagem a partir do XML
    const viagemData = {
      ambiente: 'homologacao', // Pode ser ajustado
      referencia: `XML-${chave.substring(25, 34)}`, // Usar número da NFe como referência
      data_emissao: new Date().toISOString().split('T')[0],
      tipo_cte: '0',
      natureza_operacao: 'Transporte de carga',
      tipo_servico: '0',
      modal: '01',
      cfop: '5353',
      chave_nfe: chaveNfe || (chave && chave.length === 44 ? chave : ''), // Adicionar chave NFe extraída (só se tiver 44 dígitos)
      
      destinatario: {
        cpf_cnpj: NFe.infNFe.dest?.CPF
          ? (defaultCpf + NFe.infNFe.dest.CPF).slice(-11)
          : (defaultCnpj + NFe.infNFe.dest.CNPJ).slice(-14),
        nome_razao_social: NFe.infNFe.dest?.xNome?.trim() || '',
        inscricao_estadual: NFe.infNFe.dest?.IE || '',
        endereco: {
          logradouro: NFe.infNFe.dest?.enderDest?.xLgr?.trim() || '',
          numero: NFe.infNFe.dest?.enderDest?.nro?.toString() || 'SN',
          complemento: NFe.infNFe.dest?.enderDest?.xCpl?.trim() || '',
          bairro: NFe.infNFe.dest?.enderDest?.xBairro?.trim() || '',
          cep: NFe.infNFe.dest?.enderDest?.CEP?.toString() || '',
          uf: NFe.infNFe.dest?.enderDest?.UF?.trim() || '',
          municipio: {
            codigo_ibge: '',
            descricao: NFe.infNFe.dest?.enderDest?.xMun?.trim() || '',
          },
        },
        telefones: [],
        email: NFe.infNFe.dest?.email || '',
      },
      
      remetente: {
        cpf_cnpj: (defaultCnpj + NFe.infNFe.emit?.CNPJ).slice(-14),
        nome_razao_social: NFe.infNFe.emit?.xNome?.trim() || '',
        inscricao_estadual: NFe.infNFe.emit?.IE || '',
        endereco: {
          logradouro: NFe.infNFe.emit?.enderEmit?.xLgr?.trim() || '',
          numero: NFe.infNFe.emit?.enderEmit?.nro?.toString() || 'SN',
          complemento: NFe.infNFe.emit?.enderEmit?.xCpl?.trim() || '',
          bairro: NFe.infNFe.emit?.enderEmit?.xBairro?.trim() || '',
          cep: NFe.infNFe.emit?.enderEmit?.CEP?.toString() || '',
          uf: NFe.infNFe.emit?.enderEmit?.UF?.trim() || '',
          municipio: {
            codigo_ibge: '',
            descricao: NFe.infNFe.emit?.enderEmit?.xMun?.trim() || '',
          },
        },
        telefones: [],
        email: '',
      },
      
      transporte: {
        modal: '01',
        valor_servico: parseFloat(NFe.infNFe.total?.ICMSTot?.vNF || 0),
        valor_receber: parseFloat(NFe.infNFe.total?.ICMSTot?.vNF || 0),
        valor_carga: parseFloat(NFe.infNFe.total?.ICMSTot?.vNF || 0),
        produtos_predominantes: '',
        outras_caracteristicas: '',
      },
      
      invoices: [invoice],
    };

    return viagemData;
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0) {
      setFiles(selectedFiles);
      setProgress({ processed: 0, total: selectedFiles.length, errors: [] });
    }
  };

  const handleSubmit = async () => {
    if (files.length === 0) {
      dispatch(
        toastrActions.add({
          type: 'error',
          title: 'Erro',
          message: 'Por favor, selecione pelo menos um arquivo XML',
        })
      );
      return;
    }

    setLoading(true);
    setProgress({ processed: 0, total: files.length, errors: [] });

    const errors = [];
    let successCount = 0;

    // Processar arquivos sequencialmente
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      try {
        // Ler arquivo
        const binaryStr = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(new Error('Erro ao ler arquivo'));
          reader.readAsBinaryString(file);
        });

        // Processar XML e criar estrutura da viagem
        const viagemData = processXmlToViagem(binaryStr);
        
        // Log para debug - verificar se chave_nfe está presente
        console.log('📦 Dados da viagem criada:', {
          referencia: viagemData.referencia,
          chave_nfe: viagemData.chave_nfe,
          chave_nfe_length: viagemData.chave_nfe?.length,
          destinatario: viagemData.destinatario?.nome_razao_social
        });

        // Criar viagem - aguardar callback
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Timeout ao criar viagem'));
          }, 30000); // 30 segundos timeout

          dispatch(
            TripActions.addViagemRequest(viagemData, () => {
              clearTimeout(timeout);
              successCount++;
              setProgress((prev) => ({
                ...prev,
                processed: prev.processed + 1,
              }));
              resolve();
            })
          );
        });

        // Pequeno delay entre processamentos para não sobrecarregar
        await new Promise((resolve) => setTimeout(resolve, 500));
      } catch (error) {
        console.error(`Erro ao processar ${file.name}:`, error);
        const errorMsg = error.message || 'Erro desconhecido';
        errors.push({ file: file.name, error: errorMsg });
        setProgress((prev) => ({
          ...prev,
          processed: prev.processed + 1,
          errors: [...prev.errors, { file: file.name, error: errorMsg }],
        }));
      }
    }

    setLoading(false);

    if (errors.length === 0) {
      dispatch(
        toastrActions.add({
          type: 'success',
          title: 'Sucesso',
          message: `${successCount} viagem(ns) criada(s) com sucesso!`,
        })
      );
    } else {
      dispatch(
        toastrActions.add({
          type: 'warning',
          title: 'Processamento concluído',
          message: `${successCount} viagem(ns) criada(s), ${errors.length} erro(s) encontrado(s)`,
        })
      );
    }

    if (onSuccess) {
      onSuccess({ successCount, errors });
    }

    handleClose();
  };

  const handleClose = () => {
    setFiles([]);
    setProgress({ processed: 0, total: 0, errors: [] });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const removeFile = (index) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
    setProgress((prev) => ({ ...prev, total: newFiles.length }));
  };

  return (
    <Modal open={open} onClose={handleClose} title="Importar Múltiplos XMLs">
      <Container>
        <FileInput>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xml"
            multiple
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <Button
            label="Selecionar Arquivos XML"
            onClick={handleButtonClick}
            color="secondary"
            disabled={loading}
          />
          <span style={{ marginLeft: '10px', fontSize: '14px', color: '#666' }}>
            Selecione um ou mais arquivos XML
          </span>
        </FileInput>

        {files.length > 0 && (
          <FileList>
            <h4 style={{ marginBottom: '10px' }}>Arquivos selecionados ({files.length}):</h4>
            {files.map((file, index) => (
              <FileItem key={index}>
                <span>{file.name}</span>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  disabled={loading}
                  style={{
                    background: '#f27160',
                    color: 'white',
                    border: 'none',
                    borderRadius: '3px',
                    padding: '4px 8px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                  }}
                >
                  Remover
                </button>
              </FileItem>
            ))}
          </FileList>
        )}

        {loading && (
          <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
            <p>Processando: {progress.processed} de {progress.total}</p>
            {progress.errors.length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <strong>Erros:</strong>
                {progress.errors.map((err, idx) => (
                  <div key={idx} style={{ color: 'red', fontSize: '12px' }}>
                    {err.file}: {err.error}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <WrapperButtons>
          <Button
            label="Cancelar"
            onClick={handleClose}
            color="secondary"
            margin="0 10px 0 0"
            disabled={loading}
          />
          <Button
            label={loading ? `Processando... (${progress.processed}/${progress.total})` : 'Importar e Criar Viagens'}
            onClick={handleSubmit}
            color="primary"
            disabled={loading || files.length === 0}
          />
        </WrapperButtons>
      </Container>
    </Modal>
  );
}

export default ModalImportarMultiplosXml;

